import type { Directive, DirectiveBinding, App } from 'vue'
import { usePermissionStore } from '@/store/permission'

/**
 * 权限指令
 * 用法: v-permission="'menu:create'" 或 v-permission="['menu:create', 'menu:update']"
 * 
 * 当用户没有指定权限时，元素将被移除
 */
export const permissionDirective: Directive<HTMLElement, string | string[]> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    const { value } = binding
    checkPermission(el, value)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    const { value } = binding
    checkPermission(el, value)
  }
}

/**
 * 检查权限
 */
function checkPermission(el: HTMLElement, value: string | string[]) {
  const permissionStore = usePermissionStore()
  
  if (!value) {
    return
  }
  
  let hasPermission = false
  
  if (Array.isArray(value)) {
    // 如果传入数组，检查是否拥有任一权限
    hasPermission = permissionStore.hasAnyPermission(value)
  } else {
    // 单个权限码
    hasPermission = permissionStore.hasPermission(value)
  }
  
  if (!hasPermission) {
    // 移除元素
    el.parentNode?.removeChild(el)
  }
}

/**
 * v-permission-all 指令
 * 用法: v-permission-all="['menu:create', 'menu:update']"
 * 需要拥有所有权限才会显示
 */
export const permissionAllDirective: Directive<HTMLElement, string[]> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string[]>) {
    const { value } = binding
    checkAllPermissions(el, value)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<string[]>) {
    const { value } = binding
    checkAllPermissions(el, value)
  }
}

function checkAllPermissions(el: HTMLElement, value: string[]) {
  const permissionStore = usePermissionStore()
  
  if (!value || !Array.isArray(value)) {
    return
  }
  
  const hasAllPermissions = permissionStore.hasAllPermissions(value)
  
  if (!hasAllPermissions) {
    el.parentNode?.removeChild(el)
  }
}

/**
 * 注册权限指令到 Vue 应用
 */
export function setupPermissionDirectives(app: App) {
  app.directive('permission', permissionDirective)
  app.directive('permission-all', permissionAllDirective)
}

export default permissionDirective
